import React, { useEffect } from "react";
import "./ResultPage.css";
import trophy from "../../assets/img/trophy.png";
import { useNavigate, useParams } from "react-router-dom";
import { getUrlParams } from "../../apiService/timespam";

export default function ResultPage({
  visible,
  score = 0,
  best = 0,
  correct = 0,
  wrong = 0,
  longestStreak = 0,
  fastResponses = 0,
  scoreOutOf100 = 0,
  cognitiveScore = 0,
  domainLabel = "",
  quote = "",
  onReplay = () => {},
  onShare = () => {},
}) {
  const navigate = useNavigate();
  const { uid } = useParams();

  // Signal the parent frame (AllGamesPlay) that this game is finished.
  // Old games use a backend polling mechanism; new in-app games have no such
  // signal, so we post a message as soon as the result screen appears.
  useEffect(() => {
    if (!visible) return;
    try {
      window.parent.postMessage({ type: 'GOQII_GAME_COMPLETE' }, '*');
    } catch (_) {}
  }, [visible]);

  const onGames = () => {
    // The auth params (apiKey/nonce/signature/goqiiAccessToken/appVersion/appType)
    // live nested inside the "backUrl" param's own query string, not as
    // top-level params on this game's URL -- pull them from there.
    const currentParams = new URLSearchParams(window.location.search);
    let sourceParams = currentParams;

    const backUrl = currentParams.get('backUrl'); // already decoded once by URLSearchParams
    if (backUrl) {
      const tryParse = (str) => {
        try {
          return new URL(str).searchParams;
        } catch (_) {
          return null;
        }
      };
      // Try as-is first (single-encoded, the common case); only attempt an
      // extra decode if that fails, in case backUrl was double-encoded --
      // never let a bad decode throw out of this click handler.
      let parsed = tryParse(backUrl);
      if (!parsed) {
        try {
          parsed = tryParse(decodeURIComponent(backUrl));
        } catch (_) {
          parsed = null;
        }
      }
      if (parsed) sourceParams = parsed;
    }

    const KEYS = ['uid', 'goqiiuserid', 'apiKey', 'nonce', 'signature', 'goqiiAccessToken', 'appVersion', 'appType'];
    const qs = new URLSearchParams();
    KEYS.forEach((key) => {
      const value = sourceParams.get(key) ?? currentParams.get(key);
      if (value) qs.set(key, value);
    });

    // getUrlParams() always resolves a concrete theme/lang (defaults to
    // light/en), so these are never dropped from the redirect even if this
    // game's own URL didn't carry them explicitly.
    const { theme, lang } = getUrlParams();
    qs.set('theme', theme);
    qs.set('lang', lang);

    const search = qs.toString();
    const targetUid = uid || sourceParams.get('uid') || '';
    if (targetUid) {
      navigate(search ? `/games/${targetUid}?${search}` : `/games/${targetUid}`);
    } else {
      navigate(search ? `/games?${search}` : '/games');
    }
  };

  if (!visible) return null;

  return (
    // ✅ Added scoped class "result-page-container"
    <div className="rp-overlay result-page-container" role="dialog" aria-modal="true">
      <div className="rp-card">
        
        {/* Top Section */}
        <div className="rp-top-section">
            <div className="rp-trophy"><img src={trophy} alt="Trophy" /></div>
            <div className="rp-heading">Keep playing to Improve</div>
        </div>

        {/* Score Block */}
        <div className="rp-score-block">
          <div className="rp-score-label">Score</div>
          <div className="rp-score-val">{score}</div>
          <div className="rp-best">Best: {best}</div>
          {/* {scoreOutOf100 > 0 && (
            <div className="rp-normalized-score">{scoreOutOf100}<span className="rp-normalized-denom">/100</span></div>
          )} */}
        </div>

        {/* Metrics Rows */}
        <div className="rp-metrics-wrapper">
            <div className="rp-row">
                <div className="rp-metric">
                    <div className="rp-metric-label">Correct</div>
                    <div className="rp-metric-value">{correct}</div>
                </div>

                <div className="rp-metric">
                    <div className="rp-metric-label">Wrong</div>
                    <div className="rp-metric-value">{wrong}</div>
                </div>
            </div>

            <div className="rp-row rp-row-2">
                <div className="rp-metric">
                    <div className="rp-metric-label">Longest Streak</div>
                    <div className="rp-metric-value">{longestStreak}</div>
                </div>

                <div className="rp-metric">
                    <div className="rp-metric-label">Fast Responses</div>
                    <div className="rp-metric-value">{fastResponses}</div>
                </div>
            </div>
        </div>

        {/* Cognitive Domain */}
        <div className="rp-domain-block">
          <div className="rp-domain-label">Cognitive Domain</div>
          <div className="rp-domain-value">{cognitiveScore}</div>
        </div>

        <div className="rp-domain-name">{domainLabel}</div>

        {/* Actions */}
        <div className="rp-actions">
          <button className="rp-btn-img rp-btn-share" onClick={onShare} type="button">
            Challenge Friends
          </button>

          <button className="rp-btn-img rp-btn-replay" onClick={onReplay} type="button">
            Replay
          </button>

          <button className="rp-btn-img rp-btn-share" onClick={onGames} type="button">
            More Games
          </button>
        </div>

        <div className="rp-quote">{quote}</div>
      </div>
    </div>
  );
}